/**
 * notification-actions — Surface Layer (/01).
 *
 * Shared role / decision-scope math for the notification inbox (mirror of
 * `idl_private.role_rank × decision_scope` enforced server-side by the
 * SECURITY DEFINER `notification_act` RPC). The UI uses this to decide
 * whether to show "Approve / Reject / Run" vs "Delegate up" — pure
 * presentation, never authoritative; the RPC always re-enforces.
 */

import type { NotificationRow } from '@contracts/runtime-endpoints.contract';

export const ROLE_RANK: Record<string, number> = {
  owner: 5,
  admin: 4,
  developer: 3,
  user: 2,
  viewer: 1,
};

export function roleRank(role: string | null | undefined): number {
  return ROLE_RANK[role ?? 'user'] ?? 2;
}

/** Operator-plane only: owner / admin / developer. */
export function isElevatedRole(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'admin' || role === 'developer';
}

/** Compute the rank required to act on a notification (presentation only). */
export function requiredRankFor(n: NotificationRow): number {
  const base = roleRank(n.required_role ?? 'user');
  const globalFloor = n.decision_scope === 'global' ? (ROLE_RANK.admin ?? 4) : 0;
  return Math.max(base, globalFloor);
}

/** True when the caller must escalate instead of acting directly. */
export function needsDelegate(n: NotificationRow, callerRank: number, isElevated: boolean): boolean {
  if (callerRank < requiredRankFor(n)) return true;
  if (!isElevated && (n.decision_scope === 'global' || n.actor_scope === 'hubdev')) return true;
  return false;
}

/** Map a notification to a primary action verb, if any. */
export type PrimaryActionKind = 'notify' | 'approve' | 'decision' | 'execute' | 'document' | 'stream';
export function primaryActionKind(n: NotificationRow): PrimaryActionKind {
  return (n.action_kind ?? 'notify') as PrimaryActionKind;
}
